import Vue from 'vue';
import APIBuilder from '@quangphuc/frontend-api-builder';
import Component from 'vue-class-component';
import Entity from '@/entities/entity';

export type FilterType = [string, string, any];

export type FilterGetterFn<T extends Vue = any> = (el: T) => FilterType[];

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function CreateListDataMixin<T extends Entity>(
  store: string,
  eventPrefix: string = store,
  join: string[] = [],
  order: [string, 'DESC' | 'ASC'][] = [['id', 'DESC']],
  limit = 20,
  filter: FilterType[] | FilterGetterFn = [],
  reverse = false,
) {
  @Component<ListDataMixin>({
    watch: {
      filter: {
        async handler() {
          this.apiStore.$order = [];
          this.apiStore.resetFilter();
          await this.startFetchItem();
        },
        deep: true,
      },
      order: {
        async handler() {
          this.apiStore.$order = [];
          this.apiStore.resetFilter();
          await this.startFetchItem();
        },
        deep: true,
      },
    },
  })
  class ListDataMixin extends Vue {
    limit: number = limit;
    endOfPage = false;
    rawItems: T[] = [];
    filter: [string, string, any][] = [];
    order: [string, 'DESC' | 'ASC'][] = order;
    loading = false;
    fetchFirstTime = true;

    get items(): T[] {
      if (reverse) {
        return this.rawItems.slice().reverse();
      }
      return this.rawItems;
    }

    set items(items) {
      if (reverse) {
        this.rawItems = items.slice().reverse();
      } else {
        this.rawItems = items;
      }
    }

    get scrollEl(): Window | Element {
      return window;
    }

    $refs!: {
      container: Element | Vue;
    };

    apiStore: APIBuilder<T> = APIBuilder.store<T>(store)
      .join(join)
      .page(1, this.limit);

    async created() {
      if (typeof filter === 'function') {
        this.filter = filter(this);
      } else {
        this.filter = filter;
      }
      await this.$nextTick();
      if (this.scrollEl) {
        this.scrollEl.addEventListener('scroll', this.windowsScrollHandle);
      }
      this.$cheeers.socket.on(
        `${eventPrefix}@created`,
        ({ id }: { id: number }) => {
          this.prependItem(id);
        },
      );
      this.$cheeers.socket.on(
        `${eventPrefix}@updated`,
        ({ id }: { id: number }) => {
            this.refreshItem(id);
        },
      );
    }

    async beforeDestroy() {
      if (this.scrollEl) {
        this.scrollEl.removeEventListener('scroll', this.windowsScrollHandle);
      }
      this.$cheeers.socket.off(`${eventPrefix}@created`);
      this.$cheeers.socket.off(`${eventPrefix}@updated`);
    }

    async startFetchItem() {
      if (this.fetchFirstTime) {
        await this.beforeFirstFetch();
      } else {
        await this.beforeFetch();
      }
      this.loading = true;
      this.order.forEach(([field, direction]) => {
        this.apiStore.order(field, direction);
      });
      this.filter.forEach(([field, operator, value]: [string, string, any]) => {
        this.apiStore.filter(field, operator, value);
      });
      [this.rawItems, , this.endOfPage] = await this.apiStore
        .page(1, this.limit)
        .getMany();
      this.loading = false;
      await this.$nextTick();
      if (this.fetchFirstTime) {
        if (reverse && this.scrollEl instanceof Element) {
          this.scrollEl.scrollTop = this.scrollEl.scrollHeight;
        }
        await this.afterFirstFetch();
        this.fetchFirstTime = false;
      } else {
        await this.afterFetch();
      }
    }

    async windowsScrollHandle() {
      if (!this.$refs.container || this.loading || this.endOfPage) {
        return;
      }
      let el: Element;
      if (this.$refs.container instanceof Element) {
        el = this.$refs.container;
      } else {
        el = this.$refs.container.$el;
      }

      let scrollBottom = 0;
      if (this.scrollEl instanceof Element) {
        scrollBottom =
          this.scrollEl.scrollTop + this.scrollEl.clientHeight + 30;
      }

      if (
        (this.scrollEl === window &&
          this.scrollEl.innerHeight + 150 >=
            el.getBoundingClientRect().bottom &&
          !reverse) ||
        (this.scrollEl instanceof Element &&
          scrollBottom >= this.scrollEl.scrollHeight &&
          !reverse) ||
        (this.scrollEl === window &&
          el.getBoundingClientRect().top <= 30 &&
          reverse) ||
        (this.scrollEl instanceof Element &&
          this.scrollEl.scrollTop <= 30 &&
          reverse)
      ) {
        this.loading = true;
        let rows;
        [rows, , this.endOfPage] = await this.apiStore.nextPage().getMany();
        this.rawItems = [...this.rawItems, ...rows];
        this.loading = false;
      }
    }

    async prependItem(id: number) {
      const { data } = await this.apiStore.getOne(id);
      this.rawItems = [data, ...this.rawItems];
      await this.$nextTick();
      await this.afterPrepend();
    }

    removeItem(id: number) {
      this.rawItems = this.rawItems.filter((item) => item.id !== id);
    }

    async refreshItem(id: number) {
      try {
        const { data } = await this.apiStore.getOne(id);
        if (!data) {
          this.removeItem(id);
        } else {
          this.rawItems = this.rawItems.map((post) => {
            if (post.id === id) {
              return data;
            }
            return post;
          });
        }
      } catch (e) {
        console.error(e);
        this.removeItem(id);
      }
    }

    // Hooks
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-empty-function
    async beforeFirstFetch(): Promise<any> {}
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-empty-function
    async afterFirstFetch(): Promise<any> {}
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-empty-function
    async beforeFetch(): Promise<any> {}
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-empty-function
    async afterFetch(): Promise<any> {}
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-empty-function
    async afterPrepend(): Promise<any> {}
  }

  return ListDataMixin;
}
